import React, { useEffect, useRef, useState } from "react"
import { Alert } from "reactstrap"
import { InputOrPicker } from "components/Common/DateTimeField"
import { seasonMonths, seasonWindow } from "./plannerQuery"
import { addDays } from "./plannerCalendar"
import { dayLabel, monthYearLabel } from "./plannerLabels"
import PlannerDialog from "./PlannerDialog"

const EMPTY = { date: "", name: "", source: "" }

// "Holidays" (ticket 15 Q15, PRD story 14): the legal holidays of the view's country and season, computed, the three that warn marked;
// and its declared days off („Zi liberă declarată”), each with its source, added and removed here. All are drawn on the calendar.
// `onAdd(fields)` and `onRemove(dayOff)` answer the command's result; a refusal is shown here. A day off typed and not added is kept
// until it is discarded.
export default function HolidaysDialog({ query, holidays, onClose, onAdd, onRemove }) {
  const [form, setForm] = useState(EMPTY)
  const [state, setState] = useState({})
  // Closed while a command runs: the result reaches the page, not this dialog.
  const open = useRef(true)
  useEffect(() => () => { open.current = false }, [])
  const months = seasonMonths(query.season)
  const { from, to } = seasonWindow(query.season)
  const span = `${query.country}, ${monthYearLabel(`${months[0]}-01`)} – ${monthYearLabel(`${months[months.length - 1]}-01`)}`
  const legal = (holidays || []).filter(({ kind }) => kind !== "declared")
  const declared = (holidays || []).filter(({ kind }) => kind === "declared")
  const run = async command => {
    setState({ pending: true })
    const result = await command()
    if (open.current) setState(result?.status === "saved" ? {} : { error: result?.message || "The change was not confirmed. The calendar is read again: check it." })
    return result
  }
  const add = async () => {
    const result = await run(() => onAdd({ country: query.country, date: form.date, name: form.name.trim(), source: form.source.trim() }))
    if (open.current && result?.status === "saved") setForm(EMPTY)
  }
  const field = (key, label, props = {}) => <div className="ep-field">
    <label htmlFor={"ep-day-off-" + key}>{label}</label>
    <InputOrPicker id={"ep-day-off-" + key} className="form-control" value={form[key]} onChange={event => setForm({ ...form, [key]: event.target.value })} {...props} />
  </div>

  return <PlannerDialog title="Holidays" wide closeLabel="Close" dirty={Object.values(form).some(value => value.trim())} onClose={onClose}
    sub="Legal holidays are computed for each country and year. Declared days off are added by a SuperAdmin with their source. All of them are drawn on the calendar; only Easter, Christmas and New Year give a warning.">
    <div className="ep-holidays">
      <section className="ep-stack ep-gap">
        <h3>Legal holidays (computed)</h3>
        <ul className="ep-day-list" aria-label={`Legal holidays of ${span}`}>
          {legal.map(holiday => <li key={holiday.date + holiday.name}><b>{dayLabel(holiday.date)}</b><span>{holiday.name}</span>
            {holiday.warns && <span className="ep-mark ep-mark-warn">Warns</span>}</li>)}
        </ul>
      </section>
      <section className="ep-stack ep-gap">
        <h3>Declared days off</h3>
        <ul className="ep-day-list" aria-label={`Declared days off of ${span}`}>
          {declared.map(dayOff => <li key={dayOff.id}><b>{dayLabel(dayOff.date)}</b>
            <span className="ep-stack"><span>{dayOff.name}</span><span className="ep-meta">{dayOff.source}</span></span>
            <button type="button" className="ep-icon-button" aria-label={`Remove ${dayOff.name}, ${dayLabel(dayOff.date)}`} disabled={state.pending}
              onClick={() => run(() => onRemove(dayOff))}><i className="bx bx-trash" aria-hidden="true" /></button></li>)}
        </ul>
        {!declared.length && <span className="ep-meta">No declared days off in this season.</span>}
        <div className="ep-grid">
          {field("date", "Date", { type: "date", min: from, max: addDays(to, -1) })}
          {field("name", "Name", { placeholder: "e.g. Bridge day", maxLength: 200 })}
          <div className="is-wide">{field("source", "Source reference", { placeholder: "Decision number or link", maxLength: 500 })}</div>
        </div>
        <button type="button" className="btn btn-light align-self-start" onClick={add}
          disabled={state.pending || !form.date || !form.name.trim() || !form.source.trim()}>Add declared day off</button>
        {state.error && <Alert color="danger" className="mb-0">{state.error}</Alert>}
      </section>
    </div>
  </PlannerDialog>
}
